import { ExportStatusResponse } from 'modules/export/interfaces/common-export';
import { isTerminal } from './export-status.utils';

/**
 * p99 por tipo de export, en segundos, medido sobre 16.847 jobs limpios de
 * produccion (jun-oct 2026). Es una expectativa, no una promesa: el p50 esta
 * entre 0 y 11 s, asi que la mayoria termina mucho antes.
 *
 * Solo estan los tipos con medicion suficiente. Un tipo que no figure aqui no
 * recibe estimacion: mas vale no decir nada que inventar un numero.
 */
const P99_SEGUNDOS: Record<string, number> = {
  EXPORT_SUBORDERS_PACKING_LIST: 759,
  EXPORT_STOCK: 600,
  SUBORDER_BULK_TRACKING_PAGES: 516,
  EXPORT_INVENTORY_COVERAGE_LIST: 487,
  EXPORT_LOGISTIC_CONCILIATION_PRODUCTS_LIST: 485,
  EXPORT_DETAILED_SUBORDER_LIST: 451,
  INVOICE_DELIVERY: 401,
  SUBORDER_BULK_INVOICE_DELIVERY: 398,
  EXPORT_SUBORDER_LIST: 367,
  EXPORT_PRODUCT_WITH_STOCK: 364,
  INCIDENCE_LIST: 348,
  SUBORDER_INVOICE_DELIVERY: 302,
  INVOICE: 302,
  EXPORT_DRIVER_CONCILIATION_SUBORDERS_LIST: 260,
  ORDER_INVOICE: 222,
};

export type ExportPhase = 'queued' | 'rendering' | 'finished';

export interface ExportProgress {
  phase: ExportPhase;
  /** Segundos en la fase actual. Ausente si no hay sello que medir. */
  elapsedSeconds?: number;
  /** p99 del tipo. Ausente si el tipo no tiene medicion. */
  expectedSeconds?: number;
  /** Fraccion de 0 a 1, acotada. Ausente si no se puede estimar. */
  ratio?: number;
  /** Ya paso del p99: conviene decirlo en vez de dejar la barra clavada. */
  overdue?: boolean;
}

const segundosDesde = (iso: string | undefined, ahora: number): number | undefined => {
  if (!iso) return undefined;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, Math.round((ahora - t) / 1000));
};

/**
 * Situa un export en el tiempo para poder mostrar algo mas que un spinner.
 *
 * La distincion entre cola y render importa: son dos esperas con causas
 * distintas, y hasta que el backend sello startedAt no se podian separar.
 */
export const describeProgress = (
  job: Pick<ExportStatusResponse, 'status'> & {
    createdAt?: string;
    startedAt?: string;
    finishedAt?: string;
  },
  type?: string,
  ahora: number = Date.now(),
): ExportProgress => {
  if (isTerminal(job?.status)) {
    return { phase: 'finished', elapsedSeconds: segundosDesde(job?.startedAt, ahora) };
  }

  // Sin startedAt el job sigue esperando en la cola: lo que se puede medir es
  // cuanto lleva esperando, no cuanto le queda de render.
  if (!job?.startedAt) {
    return { phase: 'queued', elapsedSeconds: segundosDesde(job?.createdAt, ahora) };
  }

  const elapsedSeconds = segundosDesde(job.startedAt, ahora);
  const expectedSeconds = type ? P99_SEGUNDOS[type] : undefined;

  if (elapsedSeconds === undefined || !expectedSeconds) {
    return { phase: 'rendering', elapsedSeconds, expectedSeconds };
  }

  // Acotado a 1: un export que pase del p99 no debe mostrar 340%.
  const ratio = Math.min(1, elapsedSeconds / expectedSeconds);

  return {
    phase: 'rendering',
    elapsedSeconds,
    expectedSeconds,
    ratio,
    overdue: elapsedSeconds > expectedSeconds,
  };
};
